import React from "react";
import PolicyLayout from "../PolicyLayout";
import { linkifyNBFC } from "../../utils/nbfcLink";

const CancellationRefundPolicy: React.FC = () => {
  const policySections = [
    {
      title: `Cancellation & Refund Policy – ${import.meta.env.VITE_COMPANY_NAME}`,
      content: `At ${import.meta.env.VITE_COMPANY_NAME}, operated by ${import.meta.env.VITE_COMPANY_NBFC}, we value your trust and are committed to maintaining a transparent and customer-friendly lending experience. This policy outlines our approach towards loan cancellations and refunds for borrowers using our platform.`,
    },
    {
      title: "1. Pre-Disbursement Cancellation",
      content: `If you change your mind before the loan amount is disbursed, you may cancel your approved application at any time without incurring any additional charges or penalties.`,
    },
    {
      title: "2. Post-Disbursement Cancellation",
      content: `If the loan amount has already been credited to your account, you may still request cancellation within 24 hours of disbursement by returning the entire principal amount to ${import.meta.env.VITE_COMPANY_NAME}. Once repayment confirmation is received, your loan will be marked as cancelled in our system.`,
    },
    {
      title: "3. Refund of Excess Payments",
      content: `Any overpayments made towards your loan account will be reconciled and refunded within 5–7 working days after review. Refunds are processed electronically to the original payment source used for repayment.`,
    },
    {
      title: "4. Processing & Timelines",
      list: [
        "Refunds are initiated only after internal verification and reconciliation.",
        "The timeline for refund reflection depends on your bank or payment gateway policy.",
        "Refund confirmation will be communicated via email/SMS registered with the company.",
      ],
    },
    {
      title: "5. Exceptions",
      list: [
        "Loans once disbursed cannot be cancelled beyond 24 hours of credit.",
        "Interest, processing fees, or service charges are non-refundable once the loan has been activated.",
        "Duplicate transactions or technical payment errors will be refunded after due verification.",
      ],
    },
    {
      title: "6. Contact & Resolution",
      content: `If you wish to raise a cancellation or refund request, please contact our support team at your registered email or through the support channels mentioned on our website. All valid refund or cancellation claims will be processed within the regulatory timelines.`,
    },
    {
      title: "Acknowledgment",
      content: `By availing our services, you acknowledge that you have read, understood, and agreed to this Cancellation & Refund Policy. Thank you for choosing ${import.meta.env.VITE_COMPANY_NAME} — your reliable partner for transparent and responsible financial services.`,
    },
  ];

  return (
    <PolicyLayout 
      title="Refund Policy" 
      subtitle={`At ${import.meta.env.VITE_COMPANY_NAME}, we strive to maintain transparency and fairness in all customer transactions. This policy explains the conditions for loan cancellations.`}
      badge="Customer Care 🤝"
    >
      {policySections.map((section, idx) => (
        <div key={idx} className="space-y-6">
          <h2 className="text-3xl font-black text-[#0F172A] tracking-tight">
            {linkifyNBFC(section.title)}
          </h2>

          {section.content && (
            <p className="text-[var(--text-secondary)] text-lg leading-relaxed font-medium whitespace-pre-line">
              {linkifyNBFC(section.content)}
            </p>
          )}
          
          {section.list && (
            <ul className="space-y-4">
              {section.list.map((li, i) => (
                <li key={i} className="flex items-start gap-3 text-[var(--text-secondary)] text-lg font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--primary)] mt-2.5 flex-shrink-0" />
                  {linkifyNBFC(li)}
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </PolicyLayout>
  );
};

export default CancellationRefundPolicy;
